import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Modal,
  Tree,
  Checkbox,
  Input,
  Progress,
  Tag,
  Typography,
  Spin,
  Alert,
  Button,
  Badge,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  FolderOutlined,
  FileOutlined,
  SearchOutlined,
  CloudUploadOutlined,
  TeamOutlined,
  FolderOpenOutlined,
  ThunderboltOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import {
  selectTenants,
  selectIsSelectiveUpgradeModalOpen,
} from "./redux/selectors";
import { setIsSelectiveUpgradeModalOpen } from "./redux/slice";
import {
  ITenant,
  IStorageNode,
  ITenantProgress,
  IUpgradeFlowResult,
} from "./redux/types";
import { getRequest } from "Utilities/ApiHelpers/ApiHelpers.index";
import { multitenantRoutes } from "Constants/api";
import LocalStorageUtility from "Utilities/localstorage";

const { Title, Text } = Typography;

type ModalPhase = "selection" | "progress" | "results";

function storageNodesToTreeData(nodes: IStorageNode[]): any[] {
  return nodes.map((node) => ({
    title: node.name,
    key: node.path,
    icon: node.isFolder ? (
      <FolderOutlined style={{ color: "#faad14" }} />
    ) : (
      <FileOutlined style={{ color: "#8c8c8c" }} />
    ),
    children: node.isFolder ? storageNodesToTreeData(node.children) : undefined,
    isLeaf: !node.isFolder,
  }));
}

function collectAllKeys(nodes: IStorageNode[]): string[] {
  const keys: string[] = [];
  for (const node of nodes) {
    keys.push(node.path);
    if (node.isFolder && node.children.length > 0) {
      keys.push(...collectAllKeys(node.children));
    }
  }
  return keys;
}

function countFiles(nodes: IStorageNode[]): number {
  let count = 0;
  for (const node of nodes) {
    if (node.isFolder) {
      count += countFiles(node.children);
    } else {
      count++;
    }
  }
  return count;
}

function getApiBaseUrl(): string {
  const storedUrl = localStorage.getItem("serverUrl");
  if (storedUrl) return storedUrl;
  return process.env.REACT_APP_SERVER_URL || "";
}

const panelStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 10,
  border: "1px solid #e8e8e8",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const panelHeaderStyle: React.CSSProperties = {
  padding: "12px 16px",
  borderBottom: "1px solid #f0f0f0",
  background: "#fafafa",
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexShrink: 0,
};

const UpgradeFlowsModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isModalOpen = useAppSelector(selectIsSelectiveUpgradeModalOpen);
  const tenantList = useAppSelector(selectTenants);

  const [phase, setPhase] = useState<ModalPhase>("selection");

  const [selectedTenantIds, setSelectedTenantIds] = useState<Set<string>>(new Set());
  const [tenantSearch, setTenantSearch] = useState("");
  const [storageTree, setStorageTree] = useState<IStorageNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedFilePaths, setSelectedFilePaths] = useState<string[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  const [tenantProgress, setTenantProgress] = useState<ITenantProgress[]>([]);
  const [totalTenants, setTotalTenants] = useState(0);

  const [results, setResults] = useState<IUpgradeFlowResult[]>([]);
  const [summary, setSummary] = useState<{
    totalTenants: number;
    successCount: number;
    failedCount: number;
  } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const totalFileCount = useMemo(() => countFiles(storageTree), [storageTree]);

  useEffect(() => {
    if (isModalOpen) {
      setSelectedTenantIds(new Set(tenantList.map((t: ITenant) => String(t.id))));
      loadStorageTree();
    }
    return () => {
      abortRef.current?.abort();
    };
  }, [isModalOpen, tenantList]);

  const loadStorageTree = async () => {
    setTreeLoading(true);
    try {
      const response = await getRequest(multitenantRoutes.ListGlobalStorageFiles, null, true);
      const data = response?.data ?? response;
      const nodes: IStorageNode[] = Array.isArray(data) ? data : [];
      setStorageTree(nodes);
      const allKeys = collectAllKeys(nodes);
      setCheckedKeys(allKeys);
      setSelectedFilePaths(allKeys);
    } catch (ex: any) {
      setError(`Failed to load files: ${ex?.message || "Unknown error"}`);
    } finally {
      setTreeLoading(false);
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    dispatch(setIsSelectiveUpgradeModalOpen(false));
    resetState();
  };

  const resetState = () => {
    setPhase("selection");
    setSelectedTenantIds(new Set());
    setTenantSearch("");
    setSelectedFilePaths([]);
    setCheckedKeys([]);
    setTenantProgress([]);
    setTotalTenants(0);
    setResults([]);
    setSummary(null);
    setError(null);
  };

  const filteredTenants = tenantList.filter((t: ITenant) =>
    t.name.toLowerCase().includes(tenantSearch.toLowerCase())
  );

  const allTenantsSelected =
    filteredTenants.length > 0 &&
    filteredTenants.every((t: ITenant) => selectedTenantIds.has(String(t.id)));

  const toggleTenant = (tenantId: string) => {
    setSelectedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
  };

  const toggleAllTenants = () => {
    if (allTenantsSelected) {
      setSelectedTenantIds(new Set());
    } else {
      setSelectedTenantIds(new Set(filteredTenants.map((t: ITenant) => String(t.id))));
    }
  };

  const onTreeCheck = (checked: any) => {
    const keys = Array.isArray(checked) ? checked : checked.checked;
    setCheckedKeys(keys);
    setSelectedFilePaths(keys.map(String));
  };

  const parseSseStream = async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.substring(7).trim();
        } else if (line.startsWith("data: ") && currentEvent) {
          try {
            const data = JSON.parse(line.substring(6));
            handleSseEvent(currentEvent, data);
          } catch {
            /* skip malformed JSON */
          }
          currentEvent = "";
        }
      }
    }
  };

  const pathIsFolderMap = useMemo(() => {
    const map = new Map<string, boolean>();
    const walk = (nodes: IStorageNode[]) => {
      for (const n of nodes) {
        map.set(n.path, n.isFolder);
        if (n.isFolder && n.children) walk(n.children);
      }
    };
    walk(storageTree);
    return map;
  }, [storageTree]);

  const startUpgrade = useCallback(async () => {
    setPhase("progress");
    setError(null);

    const selectedTenants = tenantList.filter((t: ITenant) =>
      selectedTenantIds.has(String(t.id))
    );

    const initialProgress: ITenantProgress[] = selectedTenants.map((t: ITenant) => ({
      tenantName: t.name,
      status: "pending" as const,
    }));
    setTenantProgress(initialProgress);
    setTotalTenants(selectedTenants.length);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = LocalStorageUtility.getAccessToken();
      const baseUrl = getApiBaseUrl();
      const url = `${baseUrl}${multitenantRoutes.UpgradeSelectedTenantsFlows}/`;

      const orgIds = selectedTenants
        .filter((t: any) => t.organizationId)
        .map((t: any) => t.organizationId);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tenantOrganizationIds: orgIds,
          selectedPaths: selectedFilePaths.map((p) => ({
            path: p,
            isFolder: pathIsFolderMap.get(p) ?? false,
          })),
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response stream available");

      await parseSseStream(reader);
    } catch (ex: any) {
      if (ex.name !== "AbortError") {
        setError(ex.message || "Upgrade failed");
      }
    }
  }, [selectedTenantIds, selectedFilePaths, tenantList, pathIsFolderMap]);

  const handleSseEvent = (eventType: string, data: any) => {
    switch (eventType) {
      case "tenant-start":
        setTenantProgress((prev) =>
          prev.map((p) =>
            p.tenantName === data.tenantName ? { ...p, status: "in-progress" } : p
          )
        );
        break;

      case "tenant-complete":
        setTenantProgress((prev) =>
          prev.map((p) =>
            p.tenantName === data.tenantName
              ? {
                  ...p,
                  status: data.success ? "success" : "failed",
                  errorMessage: data.errorMessage,
                }
              : p
          )
        );
        break;

      case "complete":
        setSummary({
          totalTenants: data.totalTenants,
          successCount: data.successCount,
          failedCount: data.failedCount,
        });
        setResults(data.results || []);
        setPhase("results");
        break;

      case "error":
        setError(data.message || "Unknown error");
        setPhase("results");
        break;
    }
  };

  const modalTitle = {
    selection: "Upgrade Flows — Select Tenants & Files",
    progress: "Upgrading Flows...",
    results: "Upgrade Complete",
  }[phase];

  const filePathSet = useMemo(() => {
    const set = new Set<string>();
    const walk = (nodes: IStorageNode[]) => {
      for (const n of nodes) {
        if (n.isFolder) walk(n.children);
        else set.add(n.path);
      }
    };
    walk(storageTree);
    return set;
  }, [storageTree]);

  const selectedFileCount = useMemo(
    () => selectedFilePaths.filter((p) => filePathSet.has(p)).length,
    [selectedFilePaths, filePathSet]
  );

  const renderSelection = () => (
    <div style={{ display: "flex", gap: 16, height: 460, padding: "0 0px 2px" }}>
      {/* Left panel */}
      <div style={{ ...panelStyle, width: 300, minWidth: 300, height: "100%" }}>
        <div style={panelHeaderStyle}>
          <TeamOutlined style={{ fontSize: 16, color: "#1890ff" }} />
          <Text strong>Tenants</Text>
          <Badge
            count={selectedTenantIds.size}
            style={{ backgroundColor: "#1890ff", marginLeft: "auto" }}
            overflowCount={999}
          />
        </div>
        <div style={{ padding: "10px 12px 6px", flexShrink: 0 }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Search tenants..."
            value={tenantSearch}
            onChange={(e) => setTenantSearch(e.target.value)}
            style={{ marginBottom: 8, borderRadius: 6 }}
            allowClear
            size="small"
          />
          <Checkbox
            checked={allTenantsSelected}
            indeterminate={selectedTenantIds.size > 0 && !allTenantsSelected}
            onChange={toggleAllTenants}
            style={{ padding: "4px 0" }}
          >
            <Text strong style={{ fontSize: 13 }}>
              Select All ({filteredTenants.length})
            </Text>
          </Checkbox>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 12px 10px",
            borderTop: "1px solid #f5f5f5",
          }}
        >
          {filteredTenants.map((t: ITenant, idx) => (
            <div
              key={t.id}
              style={{
                padding: "6px 8px",
                borderRadius: 6,
                cursor: "pointer",
                transition: "background 0.15s",
                background: selectedTenantIds.has(String(t.id)) ? "#e6f7ff" : idx % 2 === 0 ? "#fafafa" : "#fff",
                marginTop: idx === 0 ? 6 : 2,
              }}
              onClick={() => toggleTenant(String(t.id))}
            >
              <Checkbox
                checked={selectedTenantIds.has(String(t.id))}
                onChange={() => toggleTenant(String(t.id))}
                style={{ pointerEvents: "none" }}
              >
                <Text style={{ fontSize: 13 }}>{t.name}</Text>
              </Checkbox>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div style={{ ...panelStyle, flex: 1, height: "100%" }}>
        <div style={panelHeaderStyle}>
          <FolderOpenOutlined style={{ fontSize: 16, color: "#faad14" }} />
          <Text strong>Files & Folders</Text>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <Tag color="blue" style={{ margin: 0, borderRadius: 10, fontSize: 11 }}>
              {selectedFileCount} / {totalFileCount} files
            </Tag>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "hidden", padding: "8px 4px 8px 12px" }}>
          {treeLoading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 12,
              }}
            >
              <Spin size="large" />
              <Text type="secondary">Loading global storage files...</Text>
            </div>
          ) : storageTree.length === 0 ? (
            <Alert
              message="No files found in global storage"
              type="info"
              showIcon
              style={{ margin: 16 }}
            />
          ) : (
            <div style={{ height: "100%", overflowY: "auto", overflowX: "auto", paddingRight: 8 }}>
              <Tree
                checkable
                showIcon
                checkedKeys={checkedKeys}
                onCheck={onTreeCheck}
                treeData={storageNodesToTreeData(storageTree)}
                defaultExpandAll
                style={{ fontSize: 13 }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderProgress = () => {
    const completedCount = tenantProgress.filter(
      (p) => p.status === "success" || p.status === "failed"
    ).length;
    const inProgressCount = tenantProgress.filter(
      (p) => p.status === "in-progress"
    ).length;
    const percent = totalTenants > 0 ? Math.round((completedCount / totalTenants) * 100) : 0;

    return (
      <div>
        <div
          style={{
            background: "linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)",
            borderRadius: 10,
            padding: "20px 24px",
            marginBottom: 20,
            border: "1px solid #d6e4ff",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <SyncOutlined spin style={{ fontSize: 18, color: "#1890ff" }} />
              <Text strong style={{ fontSize: 15 }}>
                {completedCount} of {totalTenants} tenants completed
              </Text>
            </div>
            <Text type="secondary">
              {inProgressCount} in progress
            </Text>
          </div>
          <Progress
            percent={percent}
            status="active"
            strokeColor={{ from: "#1890ff", to: "#52c41a" }}
            strokeWidth={10}
            style={{ marginBottom: 0 }}
          />
        </div>

        <div
          style={{
            maxHeight: 340,
            overflowY: "auto",
            borderRadius: 10,
            border: "1px solid #e8e8e8",
          }}
        >
          {tenantProgress.map((tp, idx) => (
            <div
              key={tp.tenantName}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                background: tp.status === "in-progress"
                  ? "#e6f7ff"
                  : tp.status === "success"
                  ? "#f6ffed"
                  : tp.status === "failed"
                  ? "#fff2f0"
                  : idx % 2 === 0
                  ? "#fafafa"
                  : "#fff",
                borderBottom: idx < tenantProgress.length - 1 ? "1px solid #f0f0f0" : "none",
                transition: "background 0.3s",
              }}
            >
              {tp.status === "success" && <CheckCircleFilled style={{ color: "#52c41a", fontSize: 16 }} />}
              {tp.status === "failed" && <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 16 }} />}
              {tp.status === "in-progress" && <LoadingOutlined style={{ color: "#1890ff", fontSize: 16 }} />}
              {tp.status === "pending" && <MinusCircleOutlined style={{ color: "#d9d9d9", fontSize: 16 }} />}
              <Text style={{ flex: 1, fontSize: 13 }}>{tp.tenantName}</Text>
              {tp.status === "failed" && tp.errorMessage && (
                <Tooltip title={tp.errorMessage}>
                  <Tag color="error" style={{ fontSize: 11, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {tp.errorMessage}
                  </Tag>
                </Tooltip>
              )}
              {tp.status === "success" && (
                <Tag color="success" style={{ fontSize: 11, borderRadius: 10 }}>
                  Done
                </Tag>
              )}
              {tp.status === "in-progress" && (
                <Tag color="processing" style={{ fontSize: 11, borderRadius: 10 }}>
                  In Progress
                </Tag>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderResults = () => (
    <div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16, borderRadius: 8 }} />}
      {summary && (
        <div
          style={{
            display: "flex",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              flex: 1,
              textAlign: "center",
              padding: "16px 12px",
              borderRadius: 10,
              background: "linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)",
              border: "1px solid #d6e4ff",
            }}
          >
            <CloudUploadOutlined style={{ fontSize: 22, color: "#1890ff", marginBottom: 4 }} />
            <div style={{ fontSize: 26, fontWeight: 700, color: "#1890ff" }}>
              {summary.totalTenants}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Tenants</Text>
          </div>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              padding: "16px 12px",
              borderRadius: 10,
              background: "linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)",
              border: "1px solid #b7eb8f",
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 22, color: "#52c41a", marginBottom: 4 }} />
            <div style={{ fontSize: 26, fontWeight: 700, color: "#52c41a" }}>
              {summary.successCount}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>Successful</Text>
          </div>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              padding: "16px 12px",
              borderRadius: 10,
              background: summary.failedCount > 0
                ? "linear-gradient(135deg, #fff2f0 0%, #fff1f0 100%)"
                : "#fafafa",
              border: summary.failedCount > 0 ? "1px solid #ffa39e" : "1px solid #e8e8e8",
            }}
          >
            <CloseCircleOutlined
              style={{
                fontSize: 22,
                color: summary.failedCount > 0 ? "#ff4d4f" : "#d9d9d9",
                marginBottom: 4,
              }}
            />
            <div
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: summary.failedCount > 0 ? "#ff4d4f" : "#d9d9d9",
              }}
            >
              {summary.failedCount}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>Failed</Text>
          </div>
        </div>
      )}
      <div
        style={{
          maxHeight: 320,
          overflowY: "auto",
          borderRadius: 10,
          border: "1px solid #e8e8e8",
        }}
      >
        {results.map((r, idx) => (
          <div
            key={`${r.tenantName}-${r.organizationId}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 16px",
              background: r.success ? (idx % 2 === 0 ? "#f6ffed" : "#fff") : "#fff2f0",
              borderBottom: idx < results.length - 1 ? "1px solid #f0f0f0" : "none",
            }}
          >
            {r.success ? (
              <CheckCircleFilled style={{ color: "#52c41a", fontSize: 16 }} />
            ) : (
              <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 16 }} />
            )}
            <Text style={{ flex: 1, fontSize: 13 }}>{r.tenantName}</Text>
            {r.errorMessage && (
              <Tooltip title={r.errorMessage}>
                <Tag color="error" style={{ fontSize: 11, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.errorMessage}
                </Tag>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Modal
      title={<Title level={4} style={{ margin: 0 }}>{modalTitle}</Title>}
      open={isModalOpen}
      onCancel={handleClose}
      width={1100}
      centered={false}
      style={{ top: 30 }}
      styles={{ body: { padding: "12px 0 0", overflow: "hidden", maxHeight: "calc(100vh - 160px)" } }}
      destroyOnClose
      footer={
        phase === "selection"
          ? [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button
                key="upgrade"
                type="primary"
                icon={<ThunderboltOutlined />}
                disabled={selectedTenantIds.size === 0 || selectedFilePaths.length === 0}
                onClick={startUpgrade}
                style={{ borderRadius: 8 }}
              >
                Upgrade Selected ({selectedTenantIds.size} tenant
                {selectedTenantIds.size === 1 ? "" : "s"},{" "}
                {selectedFileCount} file
                {selectedFileCount === 1 ? "" : "s"})
              </Button>,
            ]
          : phase === "results"
          ? [
              <Button key="close" type="primary" onClick={handleClose} style={{ borderRadius: 8 }}>
                Close
              </Button>,
            ]
          : null
      }
      maskClosable={phase === "selection"}
      closable={phase !== "progress"}
    >
      {phase === "selection" && renderSelection()}
      {phase === "progress" && <div style={{ padding: "0 24px 16px" }}>{renderProgress()}</div>}
      {phase === "results" && <div style={{ padding: "0 24px 16px" }}>{renderResults()}</div>}
    </Modal>
  );
};

export default UpgradeFlowsModal;
